import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';
import { ChevronLeft, ChevronRight, Check, RotateCcw, Search, ClipboardList, Zap, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * "Walk the Racks" - phone-friendly stock-take counter.
 * One product at a time, big keypad, per-row auto-save, Prev/Next.
 * Sorted by Category → Name so you count as you move down the aisle.
 * Progress is server-persisted via PUT /api/stock-take/count/{id}.
 */
export default function WalkTheRacksCounter() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [qty, setQty] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [applying, setApplying] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Sort by category then name
  const sorted = useMemo(() => {
    return [...products].sort((a, b) => {
      const ca = (a.category || 'zzz').toLowerCase();
      const cb = (b.category || 'zzz').toLowerCase();
      if (ca !== cb) return ca.localeCompare(cb);
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [products]);

  const current = sorted[idx];

  const fetchProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/auto-spares/products`, { headers });
      setProducts(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load products');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Whenever current product changes, reset the qty input to its saved value
  useEffect(() => {
    if (current) {
      setQty(current.counted_qty !== undefined && current.counted_qty !== null
        ? String(current.counted_qty) : '');
    }
  }, [current?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const progress = useMemo(() => {
    const total = sorted.length;
    const counted = sorted.filter(p => p.counted_qty !== undefined && p.counted_qty !== null).length;
    return { total, counted, pct: total ? Math.round((counted / total) * 100) : 0 };
  }, [sorted]);

  // Captured-but-not-yet-committed counts (what the "Apply" button will push to inventory)
  const stagedSummary = useMemo(() => {
    const staged = sorted.filter(p => p.counted_qty !== undefined && p.counted_qty !== null);
    const total = staged.length;
    let newValue = 0;        // sum of counted × selling_price for staged rows
    let valueDelta = 0;      // (counted - current_qty) × selling_price
    let countDelta = 0;      // counted - current_qty
    for (const p of staged) {
      const counted = Number(p.counted_qty) || 0;
      const current = Number(p.quantity) || 0;
      const price = Number(p.selling_price) || 0;
      newValue += counted * price;
      valueDelta += (counted - current) * price;
      countDelta += (counted - current);
    }
    return { total, newValue, valueDelta, countDelta };
  }, [sorted]);

  const applyCounts = async () => {
    setApplying(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/stock-take/apply-captured`,
        { source: 'all', notes: 'Applied from Walk the Racks' },
        { headers },
      );
      toast.success(`Applied — ${res.data.rows_updated} products now match the stock take`);
      setShowApplyDialog(false);
      // Reload so staged counts clear and quantities refresh
      await fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  const saveAndAdvance = async (advance = 1) => {
    if (!current) return;
    const raw = qty.trim();
    // Persist if changed
    const persisted = current.counted_qty;
    let payload = null;
    if (raw === '') {
      if (persisted !== undefined && persisted !== null) payload = { counted_qty: null };
    } else {
      const n = parseInt(raw);
      if (isNaN(n) || n < 0) { toast.error('Enter a valid non-negative number'); return; }
      if (persisted === undefined || persisted === null || parseInt(persisted) !== n) {
        payload = { counted_qty: n };
      }
    }
    if (payload) {
      try {
        const res = await axios.put(`${API_URL}/api/stock-take/count/${current.id}`, payload, { headers });
        setProducts(prev => prev.map(p => p.id === current.id ? {
          ...p,
          counted_qty: res.data.counted_qty ?? undefined,
          counted_by: res.data.counted_by,
          counted_by_role: res.data.counted_by_role,
          counted_at: res.data.counted_at,
        } : p));
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to save');
        return;
      }
    }
    if (advance !== 0) {
      const next = idx + advance;
      if (next < 0) return;
      if (next >= sorted.length) { toast.success('End of list — you counted them all!'); return; }
      setIdx(next);
    }
  };

  // Keypad handlers
  const pushDigit = (d) => setQty(prev => (prev === '0' ? String(d) : prev + String(d)));
  const backspace = () => setQty(prev => prev.slice(0, -1));
  const clearQty = () => setQty('');

  // Skip find: jump to first product with no count yet
  const jumpToNextUncounted = () => {
    const startFrom = idx + 1;
    const found = sorted.findIndex((p, i) => i >= startFrom && (p.counted_qty === undefined || p.counted_qty === null));
    if (found < 0) {
      // wrap
      const wrap = sorted.findIndex(p => p.counted_qty === undefined || p.counted_qty === null);
      if (wrap < 0) { toast.success('All items counted!'); return; }
      setIdx(wrap);
    } else setIdx(found);
  };

  // Search jump
  const searchHits = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return [];
    return sorted
      .map((p, i) => ({ p, i }))
      .filter(({ p }) =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.supplier_sku || '').toLowerCase().includes(q))
      .slice(0, 20);
  }, [searchTerm, sorted]);

  if (loading) {
    return <div className="text-slate-300 text-center py-10">Loading catalog…</div>;
  }

  if (!current) {
    return <div className="text-slate-300 text-center py-10">No products available.</div>;
  }

  const isCounted = current.counted_qty !== undefined && current.counted_qty !== null;
  const totalValue = current.selling_price && qty ? Number(qty) * Number(current.selling_price) : 0;

  return (
    <div className="space-y-4 max-w-2xl mx-auto" data-testid="walk-the-racks">
      {/* Progress + jump controls */}
      <Card className="bg-slate-800/60 border-slate-700">
        <CardContent className="p-3 space-y-2">
          <div className="flex items-center justify-between text-xs text-slate-300">
            <span>
              <span className="text-white font-bold text-base" data-testid="wtr-progress-label">
                {progress.counted} / {progress.total}
              </span>
              <span className="text-slate-500 ml-1">counted · {progress.pct}%</span>
            </span>
            <span className="text-slate-400">
              Item <span className="text-white font-semibold">{idx + 1}</span> of {sorted.length}
            </span>
          </div>
          <div className="h-2 rounded-full bg-slate-900 border border-slate-700 overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${progress.pct === 100 ? 'bg-emerald-500' : 'bg-cyan-500'}`}
              style={{ width: `${progress.pct}%` }}
              data-testid="wtr-progress-bar"
            />
          </div>
          <div className="flex flex-wrap gap-1.5 pt-1">
            <Button size="sm" variant="outline"
              onClick={jumpToNextUncounted}
              className="bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-700 text-xs h-7"
              data-testid="wtr-jump-uncounted">
              Next uncounted →
            </Button>
            <Button size="sm" variant="outline"
              onClick={() => setSearchOpen(s => !s)}
              className="bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-700 text-xs h-7"
              data-testid="wtr-search-toggle">
              <Search className="h-3 w-3 mr-1" /> Search
            </Button>
            {stagedSummary.total > 0 && (
              <Button size="sm"
                onClick={() => setShowApplyDialog(true)}
                className="ml-auto bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7 font-semibold"
                data-testid="wtr-apply-btn">
                <Zap className="h-3 w-3 mr-1" /> Apply {stagedSummary.total} to Inventory
              </Button>
            )}
          </div>
          {stagedSummary.total > 0 && (
            <div className="pt-2 border-t border-slate-700 mt-2 flex items-center justify-between text-[11px]">
              <span className="text-slate-400">
                <span className="text-white font-semibold">{stagedSummary.total}</span> counts captured, not yet live
              </span>
              <span className={stagedSummary.valueDelta >= 0 ? 'text-emerald-400 font-mono' : 'text-rose-400 font-mono'}>
                Δ R{stagedSummary.valueDelta.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                <span className="text-slate-500 ml-1">({stagedSummary.countDelta >= 0 ? '+' : ''}{stagedSummary.countDelta} units)</span>
              </span>
            </div>
          )}
          {searchOpen && (
            <div className="pt-2 space-y-1">
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or Stock Code…"
                className="bg-slate-900 border-slate-700 text-white text-sm h-9"
                data-testid="wtr-search-input"
                autoFocus
              />
              {searchHits.length > 0 && (
                <div className="rounded border border-slate-700 max-h-48 overflow-y-auto bg-slate-900">
                  {searchHits.map(({ p, i }) => (
                    <button
                      key={p.id}
                      onClick={() => { setIdx(i); setSearchOpen(false); setSearchTerm(''); }}
                      className="w-full text-left px-3 py-2 hover:bg-slate-800 border-b border-slate-800 last:border-b-0 flex items-center justify-between"
                      data-testid={`wtr-hit-${p.supplier_sku}`}
                    >
                      <span className="text-white text-sm">{p.name}</span>
                      <span className="text-slate-500 text-xs font-mono">{p.supplier_sku}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Focus card - the item to count */}
      <Card className={`${isCounted ? 'bg-emerald-950/40 border-emerald-700' : 'bg-slate-800 border-slate-700'} border-2`}>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <Badge className="bg-slate-900 border border-slate-700 text-cyan-300 text-[10px]">
                  {current.category || 'Uncategorised'}
                </Badge>
                <span className="text-slate-500 text-xs font-mono">{current.supplier_sku}</span>
                {isCounted && (
                  <Badge className="bg-emerald-600 text-white text-[10px]"><Check className="h-3 w-3 mr-0.5" />Captured</Badge>
                )}
              </div>
              <h2 className="text-2xl font-bold text-white leading-tight break-words" data-testid="wtr-item-name">
                {current.name}
              </h2>
              <div className="text-xs text-slate-400 mt-1">
                Sell R{Number(current.selling_price || 0).toLocaleString()}
                {current.target_stock_level ? ` · Target ${current.target_stock_level}` : ''}
                {current.priority ? ` · ${current.priority}` : ''}
              </div>
            </div>
          </div>

          {/* Big qty display */}
          <div className="bg-slate-900 border-2 border-slate-700 rounded-xl p-6 text-center">
            <div className="text-[10px] uppercase text-slate-500 mb-1">On-Hand Count</div>
            <div
              className={`text-6xl font-bold tabular-nums ${qty ? 'text-cyan-300' : 'text-slate-600'}`}
              data-testid="wtr-qty-display"
            >
              {qty || '0'}
            </div>
            {qty && totalValue > 0 && (
              <div className="text-slate-500 text-xs mt-1">
                Stock value at sell: R{totalValue.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            )}
          </div>

          {/* Big keypad */}
          <div className="grid grid-cols-3 gap-2">
            {['1','2','3','4','5','6','7','8','9'].map(d => (
              <button
                key={d}
                onClick={() => pushDigit(d)}
                className="bg-slate-700 hover:bg-slate-600 text-white text-2xl font-bold rounded-lg py-4 active:scale-95 transition-transform"
                data-testid={`wtr-key-${d}`}
              >{d}</button>
            ))}
            <button
              onClick={clearQty}
              className="bg-rose-900/60 hover:bg-rose-800 text-rose-200 text-sm font-semibold rounded-lg py-4"
              data-testid="wtr-key-clear"
            ><RotateCcw className="h-5 w-5 mx-auto" /></button>
            <button
              onClick={() => pushDigit('0')}
              className="bg-slate-700 hover:bg-slate-600 text-white text-2xl font-bold rounded-lg py-4 active:scale-95 transition-transform"
              data-testid="wtr-key-0"
            >0</button>
            <button
              onClick={backspace}
              className="bg-slate-700 hover:bg-slate-600 text-slate-200 text-lg font-semibold rounded-lg py-4"
              data-testid="wtr-key-back"
            >⌫</button>
          </div>

          {/* Navigation */}
          <div className="grid grid-cols-3 gap-2 pt-1">
            <Button
              onClick={() => saveAndAdvance(-1)}
              disabled={idx === 0}
              variant="outline"
              className="bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-700 py-6 disabled:opacity-30"
              data-testid="wtr-prev-btn"
            >
              <ChevronLeft className="h-5 w-5 mr-1" /> Prev
            </Button>
            <Button
              onClick={() => saveAndAdvance(0)}
              className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold py-6"
              data-testid="wtr-save-btn"
            >
              <Check className="h-5 w-5 mr-1" /> Save
            </Button>
            <Button
              onClick={() => saveAndAdvance(1)}
              disabled={idx >= sorted.length - 1}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-6 disabled:opacity-30"
              data-testid="wtr-next-btn"
            >
              Save & Next <ChevronRight className="h-5 w-5 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <p className="text-center text-xs text-slate-500 flex items-center justify-center gap-1">
        <ClipboardList className="h-3 w-3" /> Counts save the moment you tap Save · you can pause anytime and pick up here later
      </p>

      <Dialog open={showApplyDialog} onOpenChange={setShowApplyDialog}>
        <DialogContent className="bg-slate-800 border-emerald-700 text-white max-w-md" data-testid="wtr-apply-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-300">
              <Zap className="h-5 w-5" /> Apply Stock Take to Inventory
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-300">
              This moves every captured count into live inventory. On-hand qty &amp; inventory value will update immediately.
            </p>
            <div className="bg-slate-900 border border-slate-700 rounded p-3 space-y-1.5">
              <div className="flex justify-between">
                <span className="text-slate-400">Products to update</span>
                <span className="text-white font-bold">{stagedSummary.total}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Net units change</span>
                <span className={stagedSummary.countDelta >= 0 ? 'text-emerald-400 font-mono font-bold' : 'text-rose-400 font-mono font-bold'}>
                  {stagedSummary.countDelta >= 0 ? '+' : ''}{stagedSummary.countDelta}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Inventory value change</span>
                <span className={stagedSummary.valueDelta >= 0 ? 'text-emerald-400 font-mono font-bold' : 'text-rose-400 font-mono font-bold'}>
                  {stagedSummary.valueDelta >= 0 ? '+' : ''}R{stagedSummary.valueDelta.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between pt-1 border-t border-slate-700">
                <span className="text-slate-400">New inventory value (counted lines)</span>
                <span className="text-emerald-300 font-mono font-bold">
                  R{stagedSummary.newValue.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
            <div className="flex items-start gap-2 text-xs text-amber-300 bg-amber-950/30 border border-amber-700 rounded p-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>A stock-take log row is written so this can be audited later from the Grid View &rarr; History.</span>
            </div>
          </div>
          <DialogFooter className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowApplyDialog(false)}
              className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
              data-testid="wtr-apply-cancel-btn">
              Cancel
            </Button>
            <Button onClick={applyCounts} disabled={applying}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="wtr-apply-confirm-btn">
              {applying ? 'Applying…' : <><Zap className="h-4 w-4 mr-1" /> Apply Now</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
